import { track as vercelTrack } from "@vercel/analytics";
import type { PostHog } from "posthog-js";

/**
 * Le seul point de mesure du site.
 *
 * Deux destinations pour un même geste : Vercel Analytics, qui donne les
 * volumes, et PostHog, qui donne le film de la session et le parcours. Les
 * appelants n'en savent rien : ils appellent `track()`.
 *
 * ⚠️ Rien ne part et RIEN N'EST CHARGÉ tant que la personne n'a pas accepté
 * (Loi 25, RGPD). PostHog est importé dynamiquement depuis `accorder()` : si
 * la bannière est refusée ou ignorée, le module n'est jamais téléchargé — ce
 * n'est pas seulement une question de droit, c'est aussi 250 Ko que les
 * autres visiteurs ne paient pas.
 *
 * Le quota d'enregistrement PostHog est partagé par tous les projets de
 * l'organisation, Callizy compris. Ce site a peu de trafic et les sessions y
 * répondent à une vraie question (d'où viennent les gens, ce qui les décide),
 * donc on le filme ; le back-office, lui, ne l'est plus sur ses deux gros
 * tenants.
 */

let accorde = false;
let ph: PostHog | null = null;
/**
 * L'import en cours. Sans lui, deux gestes rapprochés lancent deux imports
 * avant que `ph` ne soit posé, et PostHog se plaint d'être initialisé deux
 * fois (« Re-initializing is a no-op »). On garde la promesse, pas un
 * booléen : les appelants suivants attendent le même chargement.
 */
let chargement: Promise<void> | null = null;
/** Les gestes arrivés avant que PostHog ne soit prêt ; rejoués à l'init. */
let enAttente: Array<[string, Record<string, unknown> | undefined]> = [];

const CLE = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const HOTE = process.env.NEXT_PUBLIC_POSTHOG_HOST;

function demarrerPostHog(): Promise<void> {
  // Sans clé, le site marche exactement comme avant : pas de mesure PostHog,
  // aucune erreur. C'est le cas en développement et en avant-première.
  if (ph || !CLE || typeof window === "undefined") return Promise.resolve();
  if (chargement) return chargement;
  chargement = (async () => {
  try {
    const { default: posthog } = await import("posthog-js");
    posthog.init(CLE, {
      api_host: HOTE || "https://eu.i.posthog.com",
      // On envoie la page nous-mêmes : le routeur de Next change d'URL sans
      // recharger, et la capture automatique manquerait toute navigation
      // après la première.
      capture_pageview: false,
      persistence: "localStorage+cookie",
      session_recording: {
        // Le site porte des formulaires avec courriel et téléphone. Rien de
        // ce qui est tapé n'est filmé.
        maskAllInputs: true,
      },
    });
    ph = posthog;
    const differes = enAttente;
    enAttente = [];
    for (const [nom, props] of differes) posthog.capture(nom, props);
  } catch {
    /* Mesurer ne doit jamais casser le site. */
  }
  })();
  return chargement;
}

/** La personne a répondu à la bannière. */
export function setAnalyticsConsent(value: boolean) {
  accorde = value;
  if (value) {
    void demarrerPostHog();
  } else if (ph) {
    // Elle change d'avis : on arrête le film et on cesse d'envoyer.
    try {
      ph.stopSessionRecording();
      ph.opt_out_capturing();
    } catch {
      /* no-op */
    }
  }
}

/** Événement — ignoré tant que le consentement n'est pas donné. */
export function track(
  name: string,
  props?: Record<string, string | number | boolean | null>,
) {
  if (!accorde) return;
  try {
    vercelTrack(name, props);
  } catch {
    /* no-op */
  }
  envoyerAPostHog(name, props);
}

function envoyerAPostHog(nom: string, props?: Record<string, unknown>) {
  if (!accorde) return;
  if (!ph) {
    // L'import de PostHog est en vol : on garde le geste plutôt que de le
    // perdre. Le premier clic d'une visite tombe souvent dans cette fenêtre.
    if (enAttente.length < 30) enAttente.push([nom, props]);
    void demarrerPostHog();
    return;
  }
  try {
    ph.capture(nom, props);
  } catch {
    /* no-op */
  }
}

/**
 * Une page vue. Appelé à chaque changement de route par `MesureDesPages`,
 * puisque `capture_pageview` est coupé.
 */
export function pageview(url: string) {
  envoyerAPostHog("$pageview", { $current_url: url });
}
